// components/FileUploadModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Upload, Trash2, RotateCcw } from 'lucide-react';
import './DataWorkspace.css';
import './AlertWorkspace.css';
import WorkspacePopup from './WorkspacePopup';
import Cookies from 'js-cookie'; // Make sure to install this package: npm install js-cookie

const FileUploadModal = ({ isOpen, onClose, onUploadSuccess, onViewHistory }) => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('initial'); // initial, uploading, success, failed
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);
  const requestRef=useRef(null),busyRef=useRef(false);
  const [receipt,setReceipt]=useState(null);
  useEffect(()=>{if(!isOpen){requestRef.current?.abort();busyRef.current=false;setFile(null);setUploadStatus('initial');setReceipt(null);setErrorMessage('');}return()=>{requestRef.current?.abort();busyRef.current=false;};},[isOpen]);


  if (!isOpen) return null;

  const handleClose = () => {
    if(busyRef.current)return;
    // Reset state variables to initial state
    setFile(null);
    setUploadStatus('initial');
    setUploadProgress(0);
    setErrorMessage('');

    // Call the parent onClose function
    onClose();
  };

  const validateFileType = (file) => {
    if (!file.name.toLowerCase().endsWith('.csv') || file.size === 0) {
      setErrorMessage('Choose a non-empty CSV file');
      return false;
    }
    return true;
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (validateFileType(droppedFile)) {
        setFile(droppedFile);
        setErrorMessage('');
      } else {
        setFile(null);
        setUploadStatus('failed');
      }
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (validateFileType(selectedFile)) {
        setFile(selectedFile);
        setErrorMessage('');
      } else {
        setFile(null);
        setUploadStatus('failed');
      }
    }
  };

  const handleUpload = async () => {
    if (!file || busyRef.current) return;
    
    if (!validateFileType(file)) {
      setUploadStatus('failed');
      return;
    }

    busyRef.current=true;
    setReceipt(null);
    setUploadStatus('uploading');
    setUploadProgress(0);
    setErrorMessage('');
    
    try {
      // Get userId from cookies
      const userGuid = Cookies.get('guid');
      const token = Cookies.get('token');
      
      if (!userGuid || !token) {
        throw new Error('User ID not found. Please log in again.');
      }

      // Create FormData object to send the file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_guid', userGuid);

      // Track upload progress
      const xhr = new XMLHttpRequest();
      requestRef.current=xhr;
      xhr.timeout=120000;
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percentComplete = Math.round((event.loaded / event.total) * 100);
          setUploadProgress(percentComplete);

        }
      });

      xhr.onload = async function() {
        busyRef.current=false;
        let data;try{data=JSON.parse(xhr.responseText);}catch{data=null;}
        if(xhr.status>=200 && xhr.status<300 && data?.ok===true && data.jobId!=null){
          setReceipt(data.jobId);setUploadStatus('success');
          try{await onUploadSuccess?.();}catch{setErrorMessage('Upload received. Refresh Reports to check processing status.');}
        }else{
          setUploadStatus('failed');
          setErrorMessage(xhr.status===401||xhr.status===403?'Sign in again before uploading.':xhr.status>=200&&xhr.status<300?'The upload receipt could not be verified. Check Upload jobs before submitting again.':'Upload was not confirmed ('+xhr.status+'). Check Upload jobs before submitting again.');
        }
      };
      xhr.onerror = xhr.ontimeout = function() {
        busyRef.current=false;setUploadStatus('failed');
        setErrorMessage('Upload confirmation unavailable. Check Reports → Upload jobs before submitting again.');
      };
      // Open and send the request
      xhr.open('POST', `${process.env.REACT_APP_CYBEDEFENDER_AI_URL || '/api'}/cybedefender/upload`, true);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.send(formData);
    } catch (error) {
      busyRef.current=false;
      setUploadStatus('failed');
      setErrorMessage(error.message || 'An error occurred during upload');
    }
  };

  const handleRetry = () => {
    setUploadStatus('initial');
    setUploadProgress(0);
    setErrorMessage('');
  };

  const renderContent = () => {
    switch (uploadStatus) {
      case 'initial':
        return (
            <>
              {!file ? (
                <div 
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center"
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                >
                  <Upload className="w-8 h-8 text-blue-600 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">Choose a file or drag & drop it here</p>
                  <p className="text-gray-400 text-sm mb-4">Only .csv format, up to 50MB</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Download sample file{" "}
                    <a
                      href="/sample.csv"
                      download
                      className="text-blue-600 hover:underline"
                    >
                      here
                    </a>.
                  </p>
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Browse
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept=".csv"
                    onChange={handleFileSelect}
                  />
                </div>
              ) : (
                <div className="border rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-2 rounded">
                        <span className="text-xs font-medium">CSV</span>
                      </div>
                      <div>
                        <p className="font-medium">{file.name}</p>
                        <p className="text-sm text-gray-500">{(file.size / (1024 * 1024)).toFixed(2)}MB</p>
                      </div>
                    </div>
                    <button onClick={() => setFile(null)}>
                      <Trash2 className="w-5 h-5 text-gray-500 hover:text-red-500" />
                    </button>
                  </div>
                </div>
              )}
            </>
        );

      case 'uploading':
        return (
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-2 rounded">
                <span className="text-xs font-medium">CSV</span>
              </div>
              <div>
                <p className="font-medium">Uploading...</p>
                <p className="text-sm text-gray-500">{uploadProgress<100?"Transferring file…":"Waiting for upload receipt…"}</p>
              </div>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-200"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded">
                  <span className="text-xs font-medium">CSV</span>
                </div>
                <div>
                  <p className="font-medium">{file?.name}</p>
                  <p className="text-sm text-green-700">Upload received · processing not yet confirmed</p><p className="mt-2 text-xs text-slate-500">Job {receipt}</p><button className="mt-2 text-sm font-semibold text-cyan-800" onClick={()=>{handleClose();if(onViewHistory)onViewHistory();else window.location.assign("/reports");}}>View upload job</button>{errorMessage && <p className="mt-2 text-xs text-amber-800">{errorMessage}</p>}
                </div>
              </div>
            </div>
          </div>
        );

      case 'failed':
        return (
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded">
                  <span className="text-xs font-medium">Error</span>
                </div>
                <div>
                  <p className="font-medium">{file?.name || "Upload Failed"}</p>
                  <p className="text-sm text-red-500">
                    {errorMessage || "Upload failed. Please upload a valid .csv format."}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={handleRetry}>
                  <RotateCcw className="w-5 h-5 text-gray-500 hover:text-blue-500" />
                </button>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <WorkspacePopup isOpen={isOpen} title="Personal CSV upload" onClose={handleClose} closeLabel="Close upload" closeDisabled={uploadStatus==='uploading'} className="aq-popup-upload">
      <div className="aq-workspace">
        <div>
          {renderContent()}
        </div>
  
        {file && uploadStatus !== 'failed' && (
          <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
            {uploadStatus === 'initial' || uploadStatus === 'uploading' ? (
              <>
                <button
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                  onClick={handleClose}
                  disabled={uploadStatus === 'uploading'}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  onClick={handleUpload}
                  disabled={uploadStatus === 'uploading'}
                >
                  Upload
                </button>
              </>
            ) : (
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                onClick={handleClose}
              >
                Done
              </button>
            )}
          </div>
        )}
        
        {uploadStatus === 'failed' && (
          <div className="flex justify-end gap-2 mt-5 pt-4 border-t">
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              onClick={handleRetry}
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </WorkspacePopup>
  );
};

export default FileUploadModal;